/**
 * Returns seconds as a string in the format of `mm:ss` or `ss` if less than a minute.
 *
 * ### Example
 * ```ts
 * ToSecondsLabel(123);
 * // => "2:03"
 * ```
 *
 * @param seconds number | undefined - seconds as an int.
 * @returns string | undefined - undefined if seconds not defined or string label.
 */
export const ToSecondsLabel = (seconds?: number): string | undefined => {
  if (seconds === undefined) {
    return undefined;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

/**
 * Returns a Date as a string in the format of Fri Jan 01, 2021 or Today or Tomorrow.
 *
 * ### Example
 * ```ts
 * ToDateLabel("2023-12-28T19:13:40Z");
 * // => "Today" | "Yesterday" | "Thu Dec 28, 2023"
 * ```
 *
 * @param date string | undefined - Date as an ISO 8601 string
 * @returns string | undefined - undefined if date not defined or string label.
 */
export const ToDateLabel = (
  date: string | undefined,
  t: (val: string) => string,
  locale: Intl.LocalesArgument
): string | undefined => {
  if (date === undefined) {
    return undefined;
  }

  let label = date;

  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(now.getDate() + 1);

  const isToday = new Date(date).toDateString() === now.toDateString();
  const isYesterday =
    new Date(date).toDateString() === yesterday.toDateString();
  const isTomorrow = new Date(date).toDateString() === tomorrow.toDateString();

  if (isToday) {
    label = t("today");
  } else if (isYesterday) {
    label = t("yesterday");
  } else if (isTomorrow) {
    label = t("tomorrow");
  } else {
    label = new Date(date).toLocaleDateString(locale, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  return label;
};

/**
 * Returns a Date as a string in the format of H:MM PM.
 *
 * ### Example
 * ```ts
 * ToTimeLabel("2023-12-28T19:13:40Z");
 * // => "12:13 PM"
 * ```
 *
 * @param date string | undefined - Date as an ISO 8601 string
 * @returns string | undefined - undefined if date not defined or string label.
 */
export const ToTimeLabel = (
  date: string | undefined,
  locale: Intl.LocalesArgument
): string | undefined => {
  if (date === undefined) {
    return undefined;
  }

  const timeComponent = new Date(date).toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  });

  return timeComponent;
};

/**
 * Returns a Date as a string in the format of Fri Jan 01, 2021 or Today or Tomorrow AND H:MM PM.
 *
 * ### Example
 * ```ts
 * ToTimeLabel("2023-12-28T19:13:40Z");
 * // => "12:13 PM"
 * ```
 *
 * @param date string | undefined - Date as an ISO 8601 string
 * @returns string | undefined - undefined if date not defined or string label.
 */
export const ToDateTimeLabel = (
  date: string | undefined,
  t: (val: string) => string,
  locale: Intl.LocalesArgument
): string | undefined => {
  if (date === undefined) {
    return undefined;
  }

  const dateComponent = ToDateLabel(date, t, locale) ?? "";
  const timeComponent = ToTimeLabel(date, locale) ?? "";

  return `${dateComponent} ${timeComponent}`;
};

export const CanBeRepresentedAsHours = (minutes: number): boolean => {
  return minutes >= 60 && minutes % 15 === 0;
};
